import Button from 'ext-ui/components/Button/Button'
import React, { useEffect } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { ExtendCurrency } from 'src/enums'
import { usePostManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CurrencyResponse,
  ManagerApprovalRulesResponse,
} from 'src/models/organizations.models'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { parseBackendErrors } from 'src/utils/error.utils'
import { numberIsBelowMin } from 'src/utils/validation/number.validation'

export type ManagerReviewSettingsFormFields = {
  managerReviewEnabled?: boolean
  managerReviewUsdThresholdCents?: number
  managerReviewCadThresholdCents?: number
}

interface ManagerReviewSettingsFormProps {
  closeForm: () => void
  rules: ManagerApprovalRulesResponse | undefined
  currencies: CurrencyResponse[]
}

const ManagerReviewSettingsForm: React.FC<ManagerReviewSettingsFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { rules, currencies, closeForm } = props

  const {
    mutate: postManagerApprovalRules,
    isPending: submittingRules,
    error,
  } = usePostManagerReviewRules()
  const rulesErrors = error ? parseBackendErrors(error) : undefined

  const manageRulesFieldsValidation =
    (): FormFieldsValidation<ManagerReviewSettingsFormFields> => ({
      managerReviewUsdThresholdCents: [numberIsBelowMin(0)],
      managerReviewCadThresholdCents: [numberIsBelowMin(0)],
    })

  const {
    fields: rulesFlatFields,
    setFields: setRulesFlatFields,
    fieldsErrors: rulesFlatFieldsErrors,
    onFieldChange: rulesFlatFieldChange,
    setErrorsAndReturnDidPassValidation: rulesFlatFieldsSetErrors,
  } = useFormFields<ManagerReviewSettingsFormFields>(
    {},
    manageRulesFieldsValidation,
    rulesErrors?.errorDetailMap,
  )

  const hasCurrencyInOrg = (inputField: string): boolean => {
    let hasCurrencyInOrg = false
    currencies.some((currency) => {
      if (currency.inputField === inputField) {
        hasCurrencyInOrg = true
      }
    })

    return hasCurrencyInOrg
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (rulesFlatFieldsSetErrors()) {
      postManagerApprovalRules(
        {
          body: {
            managerReviewEnabled: !!rulesFlatFields.managerReviewEnabled,
            managerReviewUsdThresholdCents:
              rulesFlatFields.managerReviewUsdThresholdCents !== undefined
                ? toCents(rulesFlatFields.managerReviewUsdThresholdCents)
                : undefined,
            managerReviewCadThresholdCents:
              rulesFlatFields.managerReviewCadThresholdCents !== undefined
                ? toCents(rulesFlatFields.managerReviewCadThresholdCents)
                : undefined,
          },
        },
        { onSuccess: closeForm },
      )

      analytics?.track('org settings edit manager approval rules', {
        managerReviewEnabled: rulesFlatFields.managerReviewEnabled,
      })
    }
  }

  useEffect((): void => {
    setRulesFlatFields({
      ...rules,
      managerReviewUsdThresholdCents:
        rules?.managerReviewUsdThresholdCents !== undefined ||
        hasCurrencyInOrg('usdThresholdCents') ||
        currencies.length === 0
          ? toDollars(rules?.managerReviewUsdThresholdCents || 0)
          : undefined,
      managerReviewCadThresholdCents:
        rules?.managerReviewCadThresholdCents !== undefined ||
        hasCurrencyInOrg('cadThresholdCents')
          ? toDollars(rules?.managerReviewCadThresholdCents || 0)
          : undefined,
      managerReviewEnabled: !!rules?.managerReviewEnabled,
    })
  }, [rules])

  return (
    <form className={'flex-column'} onSubmit={handleOnSubmit}>
      <div className={'flex-column gap-10'}>
        <div className={'flex-column gap-9'}>
          <div className={'flex-column gap-2'}>
            <span className={'font-form-label'}>
              {translate('Manager Approver Rule')}
            </span>
            <div className={'flex-row'}>
              <OnOffSwitch
                defaultOnOffState={rules?.managerReviewEnabled}
                onChange={(val: boolean): void => {
                  rulesFlatFieldChange('managerReviewEnabled', val)
                }}
                testId={'receiptRulesSwitch'}
              />
            </div>
          </div>

          {rulesFlatFields['managerReviewEnabled'] && (
            <div className={'flex-column gap-2'}>
              <span className={'font-form-label'}>
                {translate('Minimum Manager Approver Threshold')}
              </span>
              <div className={'flex-column gap-2'}>
                {(hasCurrencyInOrg('usdThresholdCents') ||
                  currencies.length === 0) && (
                  <AmountInputField
                    baseValue={
                      rules?.managerReviewUsdThresholdCents !== undefined
                        ? toDollars(rules.managerReviewUsdThresholdCents)
                        : 0
                    }
                    value={rulesFlatFields['managerReviewUsdThresholdCents']}
                    onChange={(val: number | undefined): void => {
                      rulesFlatFieldChange(
                        'managerReviewUsdThresholdCents',
                        val !== undefined ? val : 0,
                      )
                    }}
                    errors={
                      rulesFlatFieldsErrors['managerReviewUsdThresholdCents']
                    }
                    maxValue={99999999}
                    currency={ExtendCurrency.Usd}
                    isRequest={false}
                    testId={'usdThresholdRules'}
                  />
                )}
                {hasCurrencyInOrg('cadThresholdCents') && (
                  <AmountInputField
                    baseValue={
                      rules?.managerReviewCadThresholdCents !== undefined
                        ? toDollars(rules.managerReviewCadThresholdCents)
                        : 0
                    }
                    value={rulesFlatFields['managerReviewCadThresholdCents']}
                    onChange={(val: number | undefined): void => {
                      rulesFlatFieldChange(
                        'managerReviewCadThresholdCents',
                        val !== undefined ? val : 0,
                      )
                    }}
                    errors={
                      rulesFlatFieldsErrors['managerReviewCadThresholdCents']
                    }
                    maxValue={99999999}
                    currency={ExtendCurrency.Cad}
                    isRequest={false}
                    testId={'cadThresholdRules'}
                  />
                )}
              </div>
            </div>
          )}
          {rulesErrors?.topLevelError !== undefined && (
            <div className={'font-error-message'}>
              {rulesErrors.topLevelError}
            </div>
          )}
        </div>
        <div className={'flex-row gap-5 align-center justify-start'}>
          <Button
            variant={'primary'}
            type={'submit'}
            loading={submittingRules}
            disabled={submittingRules}
            aria-label={translate('Save')}
            data-testid={'saveRulesBtn'}
          >
            {translate('Save')}
          </Button>
          <Button
            variant={'text'}
            onClick={closeForm}
            aria-label={translate('Cancel')}
            data-testid={'cancelRulesBtn'}
          >
            {translate('Cancel')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default ManagerReviewSettingsForm
